import { API } from "@swivl-mono/api-helper";
import { GetLocationsWithVariables } from "@swivl-mono/helpers";
import {
  GroupInterface,
  LocationInterface,
  LocationVarKey,
  LocationVarsWithIdByKey,
  LocationVarsWithIdByKeyByGroupOrLocationId,
  LocationWithVariablesInterface,
  OrgInterface
} from "@swivl-mono/types";
import { create } from "zustand";

import { Member } from "../Member/Member.Current";
import LocationVarsModel from "../Variables/LocationVars.Model";

interface StoreInterface {
  org?: OrgInterface;
  groups?: GroupInterface[];
  locations?: LocationInterface[];
  locationsWithVariables?: { [locationId: number]: LocationWithVariablesInterface<LocationVarKey> };
  allowedLocationsMap?: { [locationId: string]: boolean }; // Map of location IDs to enabled status
}

const BlankState: StoreInterface = {
  groups: undefined,
  locations: undefined
};

export default class LocationsModel {
  static useState = create<StoreInterface>((set) => ({ ...BlankState }));
  static setState = LocationsModel.useState.setState;
  static get state() {
    return LocationsModel.useState.getState();
  }
  static reset() {
    LocationsModel.setState({ ...BlankState });
  }
  static isLoading = false;

  static getLocation(locationId: number) {
    return LocationsModel.state.locations?.find((location) => location.id === locationId);
  }
  static getGroup(groupId: number) {
    return LocationsModel.state.groups?.find((group) => group.id === groupId);
  }

  static async getGroupsAndLocations(force: boolean) {
    if (LocationsModel.isLoading) {
      return;
    }
    if (LocationsModel.state.groups && !force) {
      return;
    }
    if (!Member.orgId) {
      return;
    }
    LocationsModel.isLoading = true;

    try {
      const groups = (await API.Locations.getGroupsAndLocations(Member.orgId))?.sort((a, b) => a.name.localeCompare(b.name));
      const locations = groups.reduce((acc, group) => [...acc, ...group.locations], [] as LocationInterface[]);
      const allowedLocationsMap: { [locationId: string]: boolean } = {};
      for (const location of locations) {
        allowedLocationsMap[location.id] = true;
      }
      if (!LocationsModel.state.locationsWithVariables && LocationVarsModel.state.isLoaded) {
        const locationsWithVariables = GetLocationsWithVariables(
          locations,
          LocationVarsModel.state.orgVariables,
          LocationVarsModel.state.groupVariables,
          LocationVarsModel.state.locationVariables
        );

        LocationsModel.setState({ groups, locations, locationsWithVariables, allowedLocationsMap });
      } else {
        LocationsModel.setState({ groups, locations, allowedLocationsMap });
      }
    } finally {
      LocationsModel.isLoading = false;
    }
  }
  static async createLocation(name: string, groupId: number) {
    if (!Member.orgId) {
      return;
    }
    return API.Locations.createLocation(Member.orgId, name, groupId).then(async (location) => {
      const locations = LocationsModel.state.locations ? [...LocationsModel.state.locations] : [];
      locations.push(location);
      const groups = LocationsModel.state.groups ? [...LocationsModel.state.groups] : [];
      for (const group of groups) {
        if (group.id === groupId) {
          if (!group.locations) {
            group.locations = [];
          }
          group.locations.push(location);
        }
      }

      LocationsModel.setState({
        locations: locations,
        groups: groups
      });
      await LocationVarsModel.loadAllVariables(true);
      return location;
    });
  }
  static async createGroup(name: string) {
    if (!Member.orgId) {
      return;
    }
    return API.Locations.createGroup(Member.orgId, name).then(async (group) => {
      const groups = LocationsModel.state.groups ? [...LocationsModel.state.groups] : [];
      if (!group.locations) {
        group.locations = [];
      }
      groups.push(group);
      LocationsModel.setState({ groups });
      await LocationVarsModel.loadAllVariables(true);
      return group;
    });
  }

  static async setGroupForLocation(locationId: number, groupId: number) {
    const oldGroup = LocationsModel.state.locations?.find((location) => location.id === locationId)?.groupId;
    const location = await API.Locations.setGroupForLocation(Member.orgId, locationId, groupId);
    const locations = LocationsModel.state.locations ? [...LocationsModel.state.locations] : [];
    for (const loc of locations) {
      if (loc.id === locationId) {
        loc.groupId = groupId;
      }
    }
    const groups = LocationsModel.state.groups ? [...LocationsModel.state.groups] : [];
    for (const group of groups) {
      if (group.id === groupId) {
        group.locations.push(location);
      }
      if (group.id === oldGroup) {
        group.locations = group.locations.filter((loc) => loc.id !== locationId);
      }
    }
    LocationsModel.setState({ locations, groups });
    return location;
  }

  static async deleteLocation(locationId: number) {
    const location = await API.Locations.deleteLocation(Member.orgId, locationId);
    let locations = LocationsModel.state.locations ? [...LocationsModel.state.locations] : [];
    locations = locations.filter((loc) => loc.id !== locationId);
    const groups = LocationsModel.state.groups ? [...LocationsModel.state.groups] : [];
    for (const group of groups) {
      group.locations = group.locations.filter((loc) => loc.id !== locationId);
    }
    LocationsModel.setState({ locations, groups });
    return location;
  }
  static async deleteGroup(groupId: number) {
    let groups = LocationsModel.state.groups ? [...LocationsModel.state.groups] : [];
    const numberOfLocationsForGroup = groups.find((group) => group.id === groupId)?.locations.length || 0;
    if (numberOfLocationsForGroup > 0) {
      alert("Group has locations");
    }
    const group = await API.Locations.deleteGroup(Member.orgId, groupId);
    groups = groups.filter((group) => group.id !== groupId);

    LocationsModel.setState({ groups });
    return group;
  }

  static updateLocationsWithVariables(
    orgVariables: LocationVarsWithIdByKey,
    groupVariables: LocationVarsWithIdByKeyByGroupOrLocationId,
    locationVariables: LocationVarsWithIdByKeyByGroupOrLocationId
  ) {
    if (!LocationsModel.state.locations) {
      return;
    }
    const locationsWithVariables = GetLocationsWithVariables(LocationsModel.state.locations, orgVariables, groupVariables, locationVariables) as {
      [locationId: number]: LocationWithVariablesInterface<LocationVarKey>;
    };
    LocationsModel.setState({ locationsWithVariables });
    return locationsWithVariables;
  }
}
